import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.details.addEventListener('toggle', this.#onRootDetailsToggle);

    // A tap before this module loads only toggles the native `open` attribute.
    // Keep that drawer visible once the component takes over styling.
    if (this.refs.details.open) {
      this.#syncOpen(this.refs.details);
    } else {
      this.refs.details.classList.remove('menu-open');
    }

    document.documentElement.classList.add('header-drawer-ready');

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    this.#setupMenuDrawerLinkNavigation();
  }

  #setupMenuDrawerLinkNavigation() {
    const menuDrawer = this.querySelector('.menu-drawer');
    if (!menuDrawer) return;

    const SCROLL_COOLDOWN_MS = 1000;
    let fingerDown = false;
    let touchMoved = false;
    let lastScrollAt = 0;
    let activeLink = null;
    /** @type {{ node: Element; top: number; left: number; }[] | null} */
    let touchScrollSnapshot = null;
    let gestureScrollClearTimer = 0;

    const scrollSelector = '.menu-drawer__navigation, .menu-drawer__inner-submenu, .menu-drawer__submenu';

    const captureScrollSnapshot = () => {
      /** @type {Element[]} */
      const nodes = [menuDrawer, ...menuDrawer.querySelectorAll(scrollSelector)];
      return nodes.map((node) => ({
        node,
        top: node.scrollTop,
        left: node.scrollLeft,
      }));
    };

    const didScrollDuringTouch = () => {
      if (!touchScrollSnapshot) return false;
      return touchScrollSnapshot.some(
        (entry) => entry.node.scrollTop !== entry.top || entry.node.scrollLeft !== entry.left
      );
    };

    const markScroll = () => {
      lastScrollAt = Date.now();
      touchMoved = true;
      menuDrawer.classList.add('menu-drawer--gesture-scroll');
    };

    const scheduleGestureScrollClear = () => {
      if (gestureScrollClearTimer) clearTimeout(gestureScrollClearTimer);
      gestureScrollClearTimer = window.setTimeout(() => {
        menuDrawer.classList.remove('menu-drawer--gesture-scroll');
      }, SCROLL_COOLDOWN_MS);
    };

    const isTapGesture = () => {
      if (touchMoved) return false;
      if (didScrollDuringTouch()) return false;
      if (Date.now() - lastScrollAt < SCROLL_COOLDOWN_MS) return false;
      if (menuDrawer.classList.contains('menu-drawer--gesture-scroll')) return false;
      return true;
    };

    const markScrollListener = () => markScroll();

    menuDrawer.addEventListener('scroll', markScrollListener, { capture: true, passive: true });
    menuDrawer.querySelectorAll(scrollSelector).forEach((el) => {
      el.addEventListener('scroll', markScrollListener, { capture: true, passive: true });
    });

    const navigate = (link, event) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') {
        this.close();
        return;
      }

      if (link.dataset.cmNavPending === 'true') return;
      link.dataset.cmNavPending = 'true';

      event.preventDefault();
      this.close();
      window.location.assign(link.href);
    };

    menuDrawer.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;

        fingerDown = true;
        touchMoved = false;
        touchScrollSnapshot = captureScrollSnapshot();

        const target = event.target;
        if (target instanceof Element) {
          const link = target.closest('a[href]');
          activeLink = link && menuDrawer.contains(link) ? link : null;
        } else {
          activeLink = null;
        }
      },
      { capture: true, passive: true }
    );

    menuDrawer.addEventListener(
      'touchmove',
      () => {
        if (!fingerDown) return;
        markScroll();
      },
      { capture: true, passive: true }
    );

    menuDrawer.addEventListener('touchcancel', () => {
      fingerDown = false;
      activeLink = null;
      touchMoved = false;
      touchScrollSnapshot = null;
    }, { capture: true, passive: true });

    menuDrawer.addEventListener(
      'touchend',
      (event) => {
        const link = activeLink;
        const validTap = isTapGesture();

        fingerDown = false;
        activeLink = null;
        touchScrollSnapshot = null;
        scheduleGestureScrollClear();

        if (!link || !validTap) return;

        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!link.contains(target) && target !== link) return;

        navigate(link, event);
      },
      { capture: true, passive: false }
    );

    menuDrawer.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const link = target.closest('a[href]');
        if (!link || !menuDrawer.contains(link)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (link.dataset.cmNavPending === 'true') return;
        if (menuDrawer.classList.contains('menu-drawer--gesture-scroll')) return;
        if (Date.now() - lastScrollAt < SCROLL_COOLDOWN_MS) return;
        if (event.pointerType !== 'mouse') return;
        if (!isTapGesture()) return;

        navigate(link, event);
      },
      { capture: true }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.details.removeEventListener('toggle', this.#onRootDetailsToggle);
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * @param {ToggleEvent} event
   */
  #onRootDetailsToggle = (event) => {
    if (event.target !== this.refs.details) return;

    const now = performance.now();
    if (now - (this.#lastRootToggleAt ?? 0) < 80) return;
    this.#lastRootToggleAt = now;

    this.toggle(event);
  };

  /** @type {number} */
  #lastRootToggleAt = 0;

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Sync drawer state when the root <details> toggles (native summary tap).
   * Uses the toggle event so iOS Safari is not fighting a capture-phase click handler.
   *
   * @param {ToggleEvent} [event]
   */
  toggle(event) {
    const details =
      event?.target instanceof HTMLDetailsElement ? event.target : this.refs.details;

    if (details !== this.refs.details) return;

    const keepOpenUntil = /** @type {number} */ (window.__cmHeaderDrawerKeepOpenUntil ?? 0);

    if (details.open) {
      window.__cmHeaderDrawerKeepOpenUntil = Date.now() + 900;
      this.#syncOpen(details);
      window.__cmStabilizeHeaderDrawerOpen?.(details);
      return;
    }

    if (Date.now() < keepOpenUntil) {
      details.open = true;
      this.#syncOpen(details);
      window.__cmStabilizeHeaderDrawerOpen?.(details);
      return;
    }

    if (details.classList.contains('menu-open')) {
      this.#syncNativeClose(details);
    }
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details && !details.open) {
      details.open = true;
      return;
    }

    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    if (details.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    trapFocus(details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #syncOpen(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    if (details.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    trapFocus(details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #syncNativeClose(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      removeTrapFocus();
      document.documentElement.removeAttribute('scroll-lock');
      this.querySelectorAll('details[open]').forEach(reset);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        document.documentElement.removeAttribute('scroll-lock');
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
